import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { prop, uniqBy } from 'ramda'
import { ActionType } from 'typesafe-actions'
/**
 * Direct module imports, not the `../user` / `../portfolio` barrels. The barrels pull in
 * each module's epics, and `api/property/epics` imports `api/portfolio`, so going through
 * them would drag this slice into a state -> barrel -> epics -> state cycle. That cycle is
 * what the `'portfolioApi/sendApplicationInvoice_success'` string key below used to work
 * around ("imports are breaking tests for some weird reason").
 */
import { events as portfolioApiEvents } from '../portfolio/state'
import { events as userApiEvents } from '../user/state'
import {
  TApplicationInvoiceStatus,
  TArchivedPeriod,
  TCreditNoteEventMeta,
  TCreditNoteEventPayload,
  TCustomerAccountActivity,
  TCustomerAccountKey,
  TEasyPayData,
  TFetchInvoicesRequest,
  TOpenInvoiceSummary,
  TPaymentRequestPayload,
  TReconInvoice,
  TReconNotification,
  TReconState,
  TUpdateBeneficiariesRequestPayload,
  TUpdateBeneficiariesResponse,
} from './types'

const initialState: TReconState = {
  invoices: [],
  creditNoteReasons: [],
  notifications: [],
  customerActiveInvoicesByAccount: {},
  customerAccountActivity: {},
  easypayData: {},
  activeInvoicesById: {},
  bulkInvoiceUploadErrors: [],
  applicationInvoicesStatus: {},
  archivedPeriod: {},
}

/**
 * Replace the stored copy of every invoice the response carries, keeping the rest of
 * the list untouched. Shared by the credit-note, payment and beneficiary-update flows,
 * which all return the affected invoices rather than the whole list.
 */
const mergeUpdatedInvoices = (state: TReconState, updated: TReconInvoice[]) => {
  state.invoices = state.invoices.map(inv => updated.find(u => u.id === inv.id) || inv)
}

const slice = createSlice({
  name: 'reconApi',
  initialState,
  reducers: {
    invoices_request: (state, { payload }: PayloadAction<TFetchInvoicesRequest>) => {},
    invoices_success: (state, { payload }: PayloadAction<TReconInvoice[]>) => {
      state.invoices = uniqBy(prop('id'), [...state.invoices, ...payload])
    },
    invoice_request: (state, { payload }: PayloadAction<{ id: string }>) => {},
    invoice_success: (state, { payload }: PayloadAction<TReconInvoice>) => {
      state.invoices = uniqBy(prop('id'), [...state.invoices, payload])
    },
    creditNoteReasons_request: () => {},
    creditNoteReasons_success: (state, { payload }: PayloadAction<TReconState['creditNoteReasons']>) => {
      state.creditNoteReasons = payload
    },
    creditNote_request: {
      reducer: (state, { payload }: PayloadAction<TCreditNoteEventPayload>) => {},
      prepare: (payload: TCreditNoteEventPayload, meta?: unknown) => ({ payload, meta, error: null }),
    },
    creditNote_success: {
      reducer: (state, { payload }: PayloadAction<TReconInvoice[], string, TCreditNoteEventMeta>) => {
        mergeUpdatedInvoices(state, payload)
      },
      prepare: (payload: TReconInvoice[], meta: TCreditNoteEventMeta) => ({ payload, meta, error: null }),
    },
    payment_request: (state, { payload }: PayloadAction<TPaymentRequestPayload>) => {},
    payment_success: (state, { payload }: PayloadAction<TReconInvoice[]>) => {
      mergeUpdatedInvoices(state, payload)
    },
    updateBenficiaries_request: (state, { payload }: PayloadAction<TUpdateBeneficiariesRequestPayload>) => {},
    updateBenficiaries_success: {
      reducer: (state, { payload }: PayloadAction<TUpdateBeneficiariesResponse, string, { invoiceId: string }>) => {
        /**
         * `invoices` is optional on the wire (the v1 fixture has no such key, and
         * `transformUpdateBeneficiariesResponse` maps it with `?.`). Reading `.find` off
         * it unguarded threw inside the reducer and tore down the dispatch.
         */
        if (payload.invoices) {
          mergeUpdatedInvoices(state, payload.invoices)
        }
      },
      prepare: (payload: TUpdateBeneficiariesResponse, meta: { invoiceId: string }) => ({
        payload,
        meta,
        error: null,
      }),
    },
    invoicesNudge_request: (state, { payload }: PayloadAction<{ invoiceIds: string[] }>) => {},
    invoicesNudge_success: (state, { payload }: PayloadAction<unknown>) => {},
    notifications_request: () => {},
    notifications_success: (state, { payload }: PayloadAction<TReconNotification[]>) => {
      state.notifications = payload
    },
    customerActiveInvoices_request: (state, { payload }: PayloadAction<TCustomerAccountKey>) => {},
    customerActiveInvoices_success: {
      reducer: (state, action: PayloadAction<TOpenInvoiceSummary[], string, TCustomerAccountKey>) => {
        const {
          payload,
          meta: { customerId, accountId },
        } = action
        state.customerActiveInvoicesByAccount[customerId] = {
          ...state.customerActiveInvoicesByAccount[customerId],
          [accountId]: payload,
        }
      },
      prepare: (payload: TOpenInvoiceSummary[], meta: TCustomerAccountKey) => ({ payload, meta, error: null }),
    },
    customerAccountActivity_request: (state, { payload }: PayloadAction<TCustomerAccountKey>) => {},
    customerAccountActivity_success: {
      reducer: (
        state,
        {
          payload,
          meta: { customerId, accountId },
        }: PayloadAction<TCustomerAccountActivity, string, TCustomerAccountKey>,
      ) => {
        state.customerAccountActivity[customerId] = {
          ...state.customerAccountActivity[customerId],
          [accountId]: payload,
        }
      },
      prepare: (payload: TCustomerAccountActivity, meta: TCustomerAccountKey) => ({ payload, meta, error: null }),
    },
    easypayData_request: (state, { payload }: PayloadAction<{ reference: string }>) => {},
    easypayData_success: {
      reducer: (state, { payload, meta }: PayloadAction<TEasyPayData, string, { reference?: string }>) => {
        if (meta?.reference) {
          state.easypayData[meta.reference] = payload
        }
      },
      prepare: (payload: TEasyPayData, meta: { reference?: string }) => ({ payload, meta, error: null }),
    },
    invoicesActive_request: (state, { payload }: PayloadAction<{ id: string }>) => {},
    invoicesActive_success: (state, { payload }: PayloadAction<{ id: string; activeInvoices: string[] }>) => {
      state.activeInvoicesById[payload.id] = payload.activeInvoices
    },
    bulkInvoiceUpload_request: (state, { payload }: PayloadAction<{ base64: string }>) => {},
    bulkInvoiceUpload_success: (state, { payload }: PayloadAction<unknown>) => {},
    bulkInvoiceUpload_failed: (state, { payload }: PayloadAction<TReconState['bulkInvoiceUploadErrors']>) => {
      state.bulkInvoiceUploadErrors = payload
    },
    applicationInvoiceByPortfolio_request: (state, { payload }: PayloadAction<{ portfolioId: string }>) => {},
    applicationInvoiceByPortfolio_success: {
      reducer: (
        state,
        { payload, meta }: PayloadAction<TApplicationInvoiceStatus[], string, { portfolioId: string }>,
      ) => {
        state.applicationInvoicesStatus[meta.portfolioId] = payload
      },
      prepare: (payload: TApplicationInvoiceStatus[], meta: { portfolioId: string }) => ({
        payload,
        meta,
        error: null,
      }),
    },
    archived_request: (state, { payload }: PayloadAction<Required<TArchivedPeriod>>) => {},
    archived_success: {
      reducer: (state, { payload, meta }: PayloadAction<TReconInvoice[], string, TArchivedPeriod>) => {
        state.archivedPeriod = meta
        state.invoices = payload
      },
      prepare: (payload: TReconInvoice[], meta: TArchivedPeriod) => ({ payload, meta, error: null }),
    },
  },
  extraReducers: builder => {
    builder
      .addCase(userApiEvents.refreshToken_success, state => {
        state.invoices = []
      })
      /**
       * Sending an application-fee invoice is a portfolio-service call, but its result is
       * an application-invoice *status* — the same row this slice stores per portfolio.
       * `portfolioApi` synthesises the row locally (it has no `customerId`), which is why
       * `TApplicationInvoiceStatus.customerId` is optional.
       */
      .addCase(portfolioApiEvents.sendApplicationInvoice_success, (state, { payload }) => {
        const portfolioStatusSlice = state.applicationInvoicesStatus[payload.portfolioId] || []
        state.applicationInvoicesStatus[payload.portfolioId] = [...portfolioStatusSlice, payload]
      })
  },
})

const { reducer, actions: events } = slice

export type ReconEvent = ActionType<typeof events>

export { events, initialState, reducer }
